import { useRef, useState } from "react";
import Modal from "../../components/ui/Modal";
import { StatusMessage } from "../../components/ui";
import { createCommsSiteNote } from "../../services/comms/comms.service";
import { getCommsErrorMessage, resolveSiteIdentifierRegistryId } from "./commsSite";
import type { CommsSite } from "./commsSite";
import { NoteList } from "./CommsSiteDetails";
import type { NoteHistory } from "./useCommsNotes";

interface Props {
    site: CommsSite;
    history: NoteHistory;
    users: Record<string, string>;
    onReload: (id: number) => Promise<NoteHistory>;
    onSaved: (message: string, warning?: string) => void;
    onClose: () => void;
}

export function CommsNoteEditor({ site, history, users, onReload, onSaved, onClose }: Props) {
    const [message, setMessage] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const inFlight = useRef(false);
    const save = async () => {
        if (inFlight.current) return;
        const id = resolveSiteIdentifierRegistryId(site);
        if (id === null) { setError("Unable to determine site identifier for notes."); return; }
        if (!message.trim()) { setError("Message is required."); return; }
        if (message.trim().length > 1000) { setError("Message must be 1000 characters or fewer."); return; }
        inFlight.current = true;
        setSubmitting(true);
        setError(null);
        let persisted = false;
        try {
            const response = await createCommsSiteNote(id, { Note: message.trim() });
            if (!response.success) throw new Error(response.error?.message || "Failed to save site message.");
            persisted = true;
            const result = await onReload(id);
            onSaved(`Message saved for ${site.siteIdentifier || site.siteName || `Site ${id}`}.`, result.error ?? undefined);
        } catch (error: unknown) {
            const text = getCommsErrorMessage(error, "Failed to save site message.");
            if (persisted) onSaved(`Message saved for ${site.siteIdentifier || site.siteName || `Site ${id}`}.`, text);
            else setError(text);
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    };
    return (
        <Modal isOpen title={`Offline Message - ${site.siteIdentifier || site.siteName || ""}`}
            onConfirm={save} onCancel={() => { if (!inFlight.current) onClose(); }}
            okButtonLoading={submitting} cancelButtonLoading={submitting}
            content={<>
                {(error || history.error) && <StatusMessage kind="error" dismissible={false}>{error || history.error}</StatusMessage>}
                <div className="form__group">
                    <label className="form__label" htmlFor="site-note-message">Message</label>
                    <textarea id="site-note-message" className="form__control" placeholder="Add a message for this offline site"
                        value={message} onChange={event => { setMessage(event.target.value); setError(null); }} maxLength={1000} disabled={submitting} />
                    <div className="form__help">{message.length}/1000</div>
                </div>
                <strong>Previous Messages</strong>
                <div className="previous-messages">
                    {history.loading && <div>Loading messages...</div>}
                    {!history.loading && !history.error && !history.notes.length && <div>No messages found.</div>}
                    {!history.loading && !!history.notes.length && <NoteList notes={history.notes} users={users} />}
                </div>
            </>} />
    );
}
